import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../contexts/TranslationContext";
import { useAuth } from "../contexts/useAuth";
import { toast } from "sonner";

function BuyPlans() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(null);
  const [searchParams] = useSearchParams();
  const [customCredits, setCustomCredits] = useState(1);

  useEffect(() => {
    if (searchParams.get("cancelled") === "true") {
      toast.info(t('buyPlans.cancelled'));
    }
  }, [searchParams]);

  const handleCheckout = async (type) => {
    setLoading(type);
    try {
      const endpoint = type === "credits"
        ? "/api/stripe/checkout-credits"
        : "/api/stripe/checkout-credits-pack";

      const body = type === "credits" ? { credits: customCredits } : {};

      const response = await fetch(`${import.meta.env.VITE_API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        const errData = ct.includes('application/json') ? await response.json() : null;
        toast.error(errData?.error || t('toasts.paymentError'));
        return;
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.error || t('toasts.paymentError'));
      }
    } catch (error) {
      console.error("Erro no checkout:", error);
      toast.error(t('toasts.checkoutError'));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex-1 bg-[#0a0a0a] text-white px-6 pt-12 pb-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-6">
          {t('buyPlans.title')}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* Pacote de Créditos Customizável */}
          <div className="relative flex flex-col h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 transition-colors hover:border-brand-400/60">
            {/* Badge slot (altura fixa p/ alinhar títulos) */}
            <div className="flex justify-center items-center h-7 mb-5">
              <span className="text-[0.7rem] tracking-[0.12em] font-bold px-3 py-1 rounded-full border border-white/15 text-gray-300">
                {t('buyPlans.flexibleBadge')}
              </span>
            </div>

            {/* Título + descrição (altura reservada) */}
            <h2 className="text-center text-2xl font-bold text-brand-500 mb-2">
              {t('buyPlans.creditsPackTitle')}
            </h2>
            <p className="text-center text-sm text-gray-400 mb-6 min-h-[2.5rem]">
              {t('buyPlans.creditsDescription')}
            </p>

            {/* Miolo: seletor de créditos */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-center mb-4">
                <span className="block text-5xl font-bold text-white leading-none">{customCredits}</span>
                <span className="block text-sm text-gray-400 mt-1">{t('buyPlans.credits', { count: customCredits })}</span>
              </div>
              <input
                type="range"
                min="1"
                max="500"
                step="1"
                value={customCredits}
                onChange={(e) => setCustomCredits(parseInt(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, rgb(var(--brand-400)) 0%, rgb(var(--brand-400)) ${((customCredits - 1) / 499) * 100}%, rgba(255,255,255,0.15) ${((customCredits - 1) / 499) * 100}%, rgba(255,255,255,0.15) 100%)`,
                }}
              />
              <div className="flex justify-between w-full text-xs text-gray-500 mt-2">
                <span>1</span>
                <span>500</span>
              </div>
            </div>

            {/* Rodapé: preço + botão (ancorado na base) */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-3xl font-bold text-green-400">
                ${customCredits}.00 <span className="text-sm font-normal text-gray-400">USD</span>
              </p>
              <p className="text-xs text-gray-500 mt-1 mb-5 min-h-[1rem]">
                $1.00 {t('buyPlans.perCredit')}
              </p>
              <button
                onClick={() => handleCheckout("credits")}
                disabled={loading !== null || customCredits < 1}
                className="w-full px-6 py-3 bg-brand-500 hover:bg-brand-400 text-black font-bold rounded-xl transition disabled:opacity-50"
              >
                {loading === "credits" ? t('buyPlans.processing') : t('buyPlans.buy')}
              </button>
            </div>
          </div>

          {/* Boost 600 (recomendado) */}
          <div className="relative flex flex-col h-full rounded-2xl border border-brand-500/40 bg-brand-500/[0.04] ring-1 ring-brand-500/20 p-8 transition-colors hover:border-brand-400/70">
            {/* Badge slot */}
            <div className="flex justify-center items-center h-7 mb-5">
              <span className="text-[0.7rem] tracking-[0.12em] font-bold px-3 py-1 rounded-full bg-brand-400 text-black">
                {t('buyPlans.bestValue')}
              </span>
            </div>

            {/* Título + descrição (altura reservada) */}
            <h2 className="text-center text-2xl font-bold text-brand-500 mb-2">
              {t('buyPlans.monthlyPlanTitle')}
            </h2>
            <p className="text-center text-sm text-gray-400 mb-6 min-h-[2.5rem]">
              {t('buyPlans.fullAccess')}
            </p>

            {/* Miolo: créditos inclusos */}
            <div className="flex-1 flex flex-col justify-center">
              <div className="text-center">
                <span className="block text-5xl font-bold text-white leading-none">600</span>
                <span className="block text-sm text-gray-400 mt-1">{t('buyPlans.credits')}</span>
              </div>
            </div>

            {/* Rodapé: preço + botão (ancorado na base) */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-3xl font-bold text-green-400">
                $175.00 <span className="text-sm font-normal text-gray-400">USD</span>
              </p>
              <p className="text-xs text-green-400 mt-1 mb-5 min-h-[1rem]">
                {t('buyPlans.savePerCredit')}
              </p>
              <button
                onClick={() => handleCheckout("pack")}
                disabled={loading !== null}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-400 hover:to-green-600 text-white font-bold rounded-xl transition disabled:opacity-50"
              >
                {loading === "pack" ? t('buyPlans.processing') : t('buyPlans.buy')}
              </button>
            </div>
          </div>
        </div>

        <p className="mt-8 text-sm text-gray-400 text-center">
          {t('buyPlans.supportEmail')}: <a href="mailto:wispervision@gmail.com" className="underline">wispervision@gmail.com</a>
        </p>
      </div>
    </div>
  );
}

export default BuyPlans;
